import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import logo from '../assets/images/hey-visuals-logo.png';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import GoogleSignInPage from './GoogleSignIn';
import { LoadingContext } from './LoadinContext';

const ConfirmResetPassword = () => {

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm_password, setConfirmPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirm_passwordError, setConfirmPasswordError] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { loadingGoogle } = useContext(LoadingContext);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pEmail = params.get('email');
    const pToken = params.get('token');

    useEffect(() => {
        if (pEmail) {
            setEmail(pEmail);
        }
    }, [pEmail]);

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setEmailError('');
        setPasswordError('');
        setConfirmPasswordError('');
        setStatus('');
        if (!email) {
            setEmailError('Email is required.');
        }
        if (!password) {
            setPasswordError('Password is required.');
        }
        if (!confirm_password) {
            setConfirmPasswordError('Confirm Password is required.');
        }
        if (password && confirm_password && password !== confirm_password) {
            setConfirmPasswordError('Passwords do not match.');
        }
        if (!email || !password || !confirm_password || password !== confirm_password) {
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/password/reset`,
                {
                    email,
                    token: pToken,
                    password,
                    'password_confirmation': confirm_password,
                }
            );
            if (response.data.status === 200) {
                setStatus(response.data.message || 'Password reset successful.');
                setEmail('');
                setPassword('');
                setConfirmPasswordError('');
                localStorage.setItem('auth', JSON.stringify(response.data.user));
                navigate('/');
            } else if (response.data.status === 422) {
                const message = response.data?.message || {};

                setEmailError(message?.email || message?.email?.[0]);
                setPasswordError(message?.password?.[0] || '');
                setConfirmPasswordError(message?.confirm_password?.[0] || '');
            } else {
                setEmailError(response.data.message || 'Failed to reset password.');
            }
        } catch (err) {
            setEmailError(err?.response?.data?.message || 'An error occurred. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRec, setShowPasswordRec] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const togglePasswordVisibilityRec = () => {
        setShowPasswordRec(!showPasswordRec);
    };

    const isEmailValid = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isPasswordValid = () => password;
    const isConfirmPasswordValid = () => confirm_password;
    const isFormValid = () => isEmailValid() && isPasswordValid() && isConfirmPasswordValid();

    return (
        <div className="d-flex flex-column flex-lg-row flex-column-fluid"
            style={{ backgroundColor: '#F7F8FB', height: '100vh' }}>
            {/* Left Section */}
            <div className="d-flex flex-lg-row-fluid">
                <div className="d-none d-md-flex flex-column flex-center pb-0 w-100">
                    <img
                        className="theme-light-show w-250px mx-auto pb-10"
                        src={logo}
                        alt="Logo"
                    />
                    {/* Video Section */}
                    <section className="HomeVideoSection">
                        <div className="HomeVideoOuter">
                            <div className="video-wrapper">
                                <div className="wistia_responsive_padding"
                                    style={{ padding: '56.25% 0 0 0', position: 'relative' }} >
                                    <div
                                        className="wistia_responsive_wrapper"
                                        style={{
                                            height: '100%', left: 0,
                                            position: 'absolute', top: 0, width: '100%',
                                        }} >
                                        <iframe
                                            src="https://fast.wistia.net/embed/iframe/dajig0c7nu?seo=true&videoFoam=true"
                                            title="How Hey Visuals AI Works? Video"
                                            allow="autoplay; fullscreen"
                                            frameBorder="0"
                                            scrolling="no"
                                            className="wistia_embed"
                                            style={{ width: '100%', height: '100%' }}
                                        ></iframe>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Right Section */}
            <div className="d-flex flex-column-fluid flex-lg-row-auto justify-content-center justify-content-lg-end p-12">
                <div className="bg-body d-flex flex-column flex-center rounded-4 w-md-600px p-10">
                    <div className="d-flex flex-center flex-column align-items-stretch h-lg-100 w-md-400px">
                        <div className="d-flex flex-center flex-column flex-column-fluid">
                            <form className="form w-100" noValidate onSubmit={handleResetPassword}>
                                <div className="text-center mb-11">
                                    <h1 className="text-dark fw-bolder mb-3">Reset Password</h1>
                                    <div className="text-gray-500 fw-semibold fs-6">
                                        Your Social Campaigns
                                    </div>
                                </div>

                                {/* Google Sign-in */}
                                <div>
                                    <GoogleSignInPage />
                                </div>

                                <div className="separator separator-content my-8">
                                    <span className="w-125px text-gray-500 fw-semibold fs-7">
                                        Reset
                                    </span>
                                </div>

                                {/* Success Message */}
                                {status && (
                                    <div className="alert alert-success reset-password-msg">
                                        {status}
                                    </div>
                                )}

                                {/* Email Input */}
                                <div className="fv-row mb-8">
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        name="email"
                                        className={`form-control form-control-solid text-muted ${emailError ? 'is-invalid' : ''}`}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled
                                        required
                                    />
                                    {emailError && <span className="text-danger">{emailError}</span>}
                                </div>

                                {/* Password Input */}
                                <div className="fv-row mb-8">
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPasswordRec ? 'text' : 'password'}
                                            placeholder="Password"
                                            name="password"
                                            className={`form-control bg-transparent ${passwordError ? 'is-invalid' : ''}`}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                        <div style={{ position: 'absolute', right: '15px', top: '6px', cursor: 'pointer' }}>
                                            <i
                                                className={`bi bi-eye-slash fs-4 text-muted ${showPasswordRec ? 'd-none' : ''}`}
                                                onClick={togglePasswordVisibilityRec}
                                            ></i>
                                            <i
                                                className={`bi bi-eye fs-4  text-primary ${showPasswordRec ? '' : 'd-none'}`}
                                                onClick={togglePasswordVisibilityRec}
                                            ></i>
                                        </div>
                                    </div>
                                    {passwordError && <span className="text-danger">{passwordError}</span>}
                                </div>

                                {/* Repeat Password Input */}
                                <div className="fv-row mb-8">
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Repeat Password"
                                            name="confirm_password"
                                            className={`form-control bg-transparent ${confirm_passwordError ? 'is-invalid' : ''}`}
                                            value={confirm_password}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                        />
                                        <div style={{ position: 'absolute', right: '15px', top: '6px', cursor: 'pointer' }}>
                                            <i
                                                className={`bi bi-eye-slash fs-4 text-muted ${showPassword ? 'd-none' : ''}`}
                                                onClick={togglePasswordVisibility}
                                            ></i>
                                            <i
                                                className={`bi bi-eye fs-4  text-primary ${showPassword ? '' : 'd-none'}`}
                                                onClick={togglePasswordVisibility}
                                            ></i>
                                        </div>
                                    </div>
                                    {confirm_passwordError && <span className="text-danger">{confirm_passwordError}</span>}
                                </div>

                                {/* Submit Button */}
                                <div className="d-grid mb-10">
                                    <button
                                        type="submit"
                                        className="btn btn-theme btn-shadow "
                                        disabled={loading || loadingGoogle || !isFormValid()}
                                    >
                                        {loading || loadingGoogle ? (
                                            loadingGoogle ? (
                                                <>
                                                    Please wait...{' '}
                                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                </>
                                            ) : (
                                                <>
                                                    Please wait...{' '}
                                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                </>
                                            )
                                        ) : (
                                            'Reset Password'
                                        )}
                                    </button>
                                </div>

                                <div className="text-gray-500 text-center fw-semibold fs-6">
                                    Already have an Account?{' '}
                                    <Link to="/login" className="link-primary">
                                        Sign in
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmResetPassword;
